import { useState } from "react";
import axios from "axios";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight, KeyRound } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n";
import { getDeviceId, setDeviceId } from "@/lib/device";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-sm text-[#0F2645] placeholder:text-[#5C6A87] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-[#3A5CC8] transition-colors duration-200";

export default function Login() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const expired = searchParams.get("expire") === "1";

  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");

  const finishLogin = (data) => {
      if (data.device_id) setDeviceId(data.device_id);
      if (!["artisan", "pro", "entreprise", "admin"].includes(data.role)) {
        setError(t("Devisto est réservé aux artisans et entreprises du bâtiment."));
        return;
      }
      localStorage.setItem("devisto_token", data.token);
      localStorage.setItem("devisto_role", data.role);
      localStorage.setItem("devisto_name", data.name || "");
      localStorage.setItem("devisto_token_at", String(Date.now()));
      localStorage.setItem("devisto_member_role", data.member_role || "");
      if (data.media_token) localStorage.setItem("devisto_media_token", data.media_token);
      if (data.role === "admin") {
        localStorage.setItem("devisto_admin_token", data.token);
        navigate("/admin");
      } else if (data.member_role === "comptable") {
        navigate("/studio/comptable");
      } else {
        navigate(redirect && redirect.startsWith("/studio") ? redirect : "/studio");
      }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, device_id: getDeviceId() });
      if (data.mfa_required) { setMfa(data); return; }
      finishLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("Connexion impossible. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login/verify`, { challenge: mfa.challenge, code, remember_device: true, device_id: getDeviceId() });
      finishLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("Code invalide. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
        <div className="w-full max-w-md">
          <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8] text-center">{t("Espace membres")}</p>
          <h1 data-testid="login-title" className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center">
            {t("Connexion")}
          </h1>
          <p className="mt-2 text-sm text-[#5C6A87] text-center">
            {t("Artisans et entreprises du bâtiment : accédez à votre Studio Devisto.")}
          </p>
          {mfa ? (
          <form onSubmit={verify} data-testid="mfa-form" className="mt-8 rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-6 sm:p-8 space-y-5">
            <p className="text-sm text-[#5C6A87]">{t("Un code à 6 chiffres vient d'être envoyé à")} <span className="font-semibold text-[#0F2645]">{mfa.email_hint}</span>. {t("Il est valable 10 minutes.")}</p>
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium mb-2">{t("Code de connexion")}</label>
              <input id="mfa-code" data-testid="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required autoFocus />
            </div>
            {error && <p data-testid="mfa-error" className="text-sm text-red-600">{error}</p>}
            <button type="submit" data-testid="mfa-submit" disabled={loading || code.length !== 6} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#3A5CC8] px-6 py-3 text-sm font-semibold text-white hover:bg-[#2F4CA8] disabled:opacity-50">{loading ? t("Vérification…") : t("Valider et se connecter")}</button>
            <button type="button" data-testid="mfa-back" onClick={() => { setMfa(null); setCode(""); setError(""); }} className="w-full text-sm text-[#5C6A87] hover:text-[#0F2645]">{t("Retour")}</button>
          </form>
          ) : (
          <form onSubmit={submit} className="mt-8 rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-6 sm:p-8 space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Email")}</label>
              <input id="login-email" type="email" data-testid="login-email" className={inputCls} placeholder="vous@exemple.be" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Mot de passe")}</label>
              <input id="login-password" type="password" data-testid="login-password" className={inputCls} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {expired && !error && (
              <p data-testid="login-expired" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t("Votre session a expiré après une longue inactivité. Reconnectez-vous pour reprendre là où vous étiez.")}
              </p>
            )}
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] hover:bg-[#3A5CC8] disabled:opacity-40 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
            >
              {loading ? t("Connexion…") : t("Se connecter")} <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/mot-de-passe-oublie"
              data-testid="login-forgot-link"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#E2E7F7] bg-white px-8 py-3 text-sm font-semibold text-[#0F2645] hover:border-[#3A5CC8] hover:text-[#3A5CC8] transition-colors duration-200"
            >
              <KeyRound className="w-4 h-4" /> {t("Mot de passe oublié ?")}
            </Link>
            <p className="text-sm text-[#5C6A87] text-center">
              {t("Pas encore de compte ?")}{" "}
              <Link to="/studio/inscription" data-testid="login-register-artisan-link" className="text-[#3A5CC8] hover:text-[#2F4CA8] font-medium">
                {t("Créer mon compte artisan")}
              </Link>
            </p>
          </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
